import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Grid, MenuItem } from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useSetting } from '../../../contexts/SettingContext';
import { IService } from '../../../interfaces/Setting.interface';
import { IMember } from '../../../interfaces/Member.interface';
import { Role, ServiceType } from '../../../interfaces/constants';
import { Autocomplete } from '@mui/material';
import { useFetch } from '../../../utils/FetchTemplate';

interface AddServiceToMemberDialogProps {
  open: boolean;
  member: IMember | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormValues {
  service: IService | null;
  // shared
  discountType: 'none' | 'percent' | 'fixed';
  discountValue?: number;
  // vip
  pricePerHour?: number;
  hoursPurchased?: number;
}

const priceOptions = Array.from({ length: 21 }, (_, i) => 100 + i * 10);

const AddServiceToMemberDialog: React.FC<AddServiceToMemberDialogProps> = ({ open, member, onClose, onSuccess }) => {
  const { settings } = useSetting();
  const { postData } = useFetch();
  const { control, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm<FormValues>({
    defaultValues: {
      service: null,
      discountType: 'none',
      discountValue: 0,
      pricePerHour: undefined,
      hoursPurchased: undefined,
    }
  });

  const selectedService = watch('service');
  const discountType = watch('discountType');

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  useEffect(() => {
    if (selectedService?.type === ServiceType.VIP) {
      // lock price to service.price
      setValue('pricePerHour', selectedService.price);
    } else {
      setValue('pricePerHour', undefined);
      setValue('hoursPurchased', undefined);
    }
  }, [selectedService, setValue]);

  const isStudent = member?.role === Role.Student;
  const services = useMemo(() => settings.services || [], [settings.services]);

  const onSubmit = async (data: FormValues) => {
    if (!member || !data.service) return;
    const service = data.service;
    try {
      if (service.type === ServiceType.Group) {
        await postData('/api/setting/addStudentToService', {
          userId: settings.userId,
          data: {
            serviceId: service.serviceId,
            memberId: member.memberId,
            discountType: data.discountType,
            discountAmount: Number(data.discountValue || 0),
          },
        });
      } else if (service.type === ServiceType.VIP) {
        await postData('/api/setting/enrollVipService', {
          userId: settings.userId,
          data: {
            serviceId: service.serviceId,
            memberId: member.memberId,
            discountType: data.discountType,
            discountAmount: Number(data.discountValue || 0),
            pricePerHour: service.price,
            hoursPurchased: Number(data.hoursPurchased || 0),
          },
        });
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to add service to member', err);
    }
  };

  return (
    <Dialog open={open && isStudent} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Service to Student</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="service"
                control={control}
                rules={{ required: 'Service is required' }}
                render={({ field }) => (
                  <Autocomplete
                    options={services}
                    getOptionLabel={(option: any) => `${option.name} / ${option.type}`}
                    isOptionEqualToValue={(a: any, b: any) => a.serviceId === b.serviceId}
                    value={field.value}
                    onChange={(_, value) => field.onChange(value)}
                    renderInput={(params) => (
                      <TextField {...params} label="Service" fullWidth error={!!errors.service} helperText={(errors.service?.message as string) || ''} />
                    )}
                  />
                )}
              />
            </Grid>

            {/* Discount */}
            <Grid item xs={6}>
              <Controller
                name="discountType"
                control={control}
                render={({ field }) => (
                  <TextField select label="Discount Type" fullWidth {...field}>
                    <MenuItem value="none">None</MenuItem>
                    <MenuItem value="percent">Percentage</MenuItem>
                    <MenuItem value="fixed">Fixed Value</MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                name="discountValue"
                control={control}
                rules={{ min: { value: discountType === 'none' ? 0 : 0.01, message: 'Discount must be > 0' } }}
                render={({ field }) => (
                  <TextField type="number" label="Discount Value" fullWidth disabled={discountType === 'none'} {...field} value={discountType === 'none' ? 0 : field.value || ''} />
                )}
              />
            </Grid>

            {/* VIP fields */}
            {selectedService?.type === ServiceType.VIP && (
              <>
                <Grid item xs={6}>
                <Controller
                  name="pricePerHour"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      label="Price (CAD/hr)"
                      fullWidth
                      value={field.value ?? ''}
                      InputProps={{ readOnly: true }}
                      variant="outlined"
                    />
                  )}
                />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="hoursPurchased"
                    control={control}
                    rules={{ required: 'Hours are required', min: { value: 0.1, message: 'Hours must be > 0' } }}
                    render={({ field }) => (
                      <TextField type="number" label="Hours Purchased" fullWidth {...field} />
                    )}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddServiceToMemberDialog; 